import { Head, router } from '@inertiajs/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { RefObject } from 'react'
import { AlertTriangle, Calendar, CheckCircle2, Download, Search, ShieldAlert, Wallet } from 'lucide-react'
import Pagination from '@/components/pagination.tsx'
import { Input } from '@/components/ui/input.tsx'
import { Card } from '@/components/ui/card.tsx'
import { Button } from '@/components/ui/button.tsx'

interface FinancialAuditRow {
  id: string
  code: string
  status: string
  started_at?: string | null
  ended_at?: string | null
  community_name?: string | null
  agent_name?: string | null
  device_name?: string | null
  total_payments: number
  expected_amount: number
  declared_amount: number
  gap_amount: number
  gap_abs_amount: number
  audit_status: 'balanced' | 'warning' | 'critical'
}

interface PaginationType {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
  next_page: number | null
  prev_page: number | null
}

interface IndexProps {
  audits: FinancialAuditRow[]
  pagination: PaginationType
  stats: {
    total_sessions: number
    balanced_sessions: number
    warning_sessions: number
    critical_sessions: number
    total_expected: number
    total_declared: number
    total_gap_abs: number
  }
  filters: {
    term: string
    community_id: string
    status: string
    date_from: string
    date_to: string
  }
  filter_options: {
    communities: Array<{ id: string; name: string }>
    statuses: string[]
  }
}

const formatMoney = (amount: number) => `${amount.toLocaleString()} FCFA`

export default function Index({ audits, pagination, stats, filters, filter_options }: IndexProps) {
  const hasInitialized = useRef(false)
  const fromDateInputRef = useRef<HTMLInputElement>(null)
  const toDateInputRef = useRef<HTMLInputElement>(null)
  const [term, setTerm] = useState(filters.term)
  const [communityId, setCommunityId] = useState(filters.community_id)
  const [status, setStatus] = useState(filters.status)
  const [dateFrom, setDateFrom] = useState(filters.date_from)
  const [dateTo, setDateTo] = useState(filters.date_to)

  useEffect(() => {
    setTerm(filters.term)
    setCommunityId(filters.community_id)
    setStatus(filters.status)
    setDateFrom(filters.date_from)
    setDateTo(filters.date_to)
  }, [filters.community_id, filters.date_from, filters.date_to, filters.status, filters.term])

  const query = useMemo(() => ({
    term,
    community_id: communityId,
    status,
    date_from: dateFrom,
    date_to: dateTo
  }), [communityId, dateFrom, dateTo, status, term])

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true
      return
    }

    if (
      term === filters.term &&
      communityId === filters.community_id &&
      status === filters.status &&
      dateFrom === filters.date_from &&
      dateTo === filters.date_to
    ) {
      return
    }

    const timeoutId = setTimeout(() => {
      router.get('/admin/financial_audits', { ...query, page: 1 }, {
        preserveState: true,
        preserveScroll: true,
        replace: true
      })
    }, 350)

    return () => clearTimeout(timeoutId)
  }, [communityId, dateFrom, dateTo, filters.community_id, filters.date_from, filters.date_to, filters.status, filters.term, query, status, term])

  const openDatePicker = (inputRef: RefObject<HTMLInputElement | null>) => {
    const input = inputRef.current as (HTMLInputElement & { showPicker?: () => void }) | null
    input?.showPicker?.()
  }

  const formatSessionStatus = (value: string) => {
    if (value === 'open') return 'Ouvert'
    if (value === 'closed') return 'Fermé'
    if (value === 'reconciled') return 'Rapproché'
    return value
  }

  const exportCsv = () => {
    const params = new URLSearchParams()
    if (term) params.set('term', term)
    if (communityId) params.set('community_id', communityId)
    if (status) params.set('status', status)
    if (dateFrom) params.set('date_from', dateFrom)
    if (dateTo) params.set('date_to', dateTo)

    const queryString = params.toString()
    const url = `/admin/financial_audits.csv${queryString ? `?${queryString}` : ''}`
    window.location.assign(url)
  }

  return (
    <>
      <Head title="Audit financier" />

      <div className="space-y-6 p-8">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-3xl font-bold text-foreground">Audit financier</h2>
            <p className="text-sm text-muted-foreground">Contrôle des écarts entre collectes déclarées et paiements enregistrés.</p>
          </div>
          <Button type="button" variant="outline" className="gap-2" onClick={exportCsv}>
            <Download className="h-4 w-4" />
            Exporter CSV
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <Card className="border-border bg-card p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Sessions auditées</p>
                <p className="mt-1 text-2xl font-bold text-foreground">{stats.total_sessions}</p>
              </div>
              <Wallet className="h-7 w-7 text-primary" />
            </div>
          </Card>
          <Card className="border-border bg-card p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Équilibrées</p>
                <p className="mt-1 text-2xl font-bold text-success">{stats.balanced_sessions}</p>
              </div>
              <CheckCircle2 className="h-7 w-7 text-success" />
            </div>
          </Card>
          <Card className="border-border bg-card p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avertissements</p>
                <p className="mt-1 text-2xl font-bold text-warning">{stats.warning_sessions}</p>
              </div>
              <AlertTriangle className="h-7 w-7 text-warning" />
            </div>
          </Card>
          <Card className="border-border bg-card p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Critiques</p>
                <p className="mt-1 text-2xl font-bold text-destructive">{stats.critical_sessions}</p>
              </div>
              <ShieldAlert className="h-7 w-7 text-destructive" />
            </div>
          </Card>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <Card className="border-border bg-card p-4">
            <p className="text-sm text-muted-foreground">Montant attendu (paiements)</p>
            <p className="mt-1 text-xl font-semibold text-foreground">{formatMoney(stats.total_expected || 0)}</p>
          </Card>
          <Card className="border-border bg-card p-4">
            <p className="text-sm text-muted-foreground">Montant déclaré (terrain)</p>
            <p className="mt-1 text-xl font-semibold text-foreground">{formatMoney(stats.total_declared || 0)}</p>
          </Card>
          <Card className="border-border bg-card p-4">
            <p className="text-sm text-muted-foreground">Écart absolu total</p>
            <p className="mt-1 text-xl font-semibold text-destructive">{formatMoney(stats.total_gap_abs || 0)}</p>
          </Card>
        </div>

        <Card className="border-border bg-card p-6">
          <div className="grid gap-4 md:grid-cols-5">
            <div>
              <label className="text-xs text-muted-foreground">Recherche</label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-10"
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  placeholder="Code, agent, terminal..."
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Communauté</label>
              <select
                className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                value={communityId}
                onChange={(e) => setCommunityId(e.target.value)}
              >
                <option value="">Toutes</option>
                {filter_options.communities.map((community) => (
                  <option key={community.id} value={community.id}>{community.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Statut session</label>
              <select
                className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="">Tous</option>
                {filter_options.statuses.map((sessionStatus) => (
                  <option key={sessionStatus} value={sessionStatus}>{formatSessionStatus(sessionStatus)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Du</label>
              <div className="relative mt-1">
                <Input
                  ref={fromDateInputRef}
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="pr-10"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground hover:bg-secondary"
                  onClick={() => openDatePicker(fromDateInputRef)}
                >
                  <Calendar className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Au</label>
              <div className="relative mt-1">
                <Input
                  ref={toDateInputRef}
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="pr-10"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground hover:bg-secondary"
                  onClick={() => openDatePicker(toDateInputRef)}
                >
                  <Calendar className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </Card>

        <Card className="border-border bg-card">
          <div className="overflow-hidden rounded-lg">
            <table className="w-full">
              <thead className="border-b border-border bg-secondary">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Session</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Agent / Terminal</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Paiements</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Attendu</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Déclaré</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Écart</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Audit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {audits.length === 0 && (
                  <tr>
                    <td className="px-4 py-6 text-sm text-muted-foreground" colSpan={7}>
                      Aucune session trouvée pour ces filtres.
                    </td>
                  </tr>
                )}
                {audits.map((audit) => (
                  <tr key={audit.id} className="hover:bg-secondary/50">
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-foreground">{audit.code || '-'}</p>
                      <p className="text-xs text-muted-foreground">{audit.community_name || '-'}</p>
                      <p className="text-xs text-muted-foreground">
                        {audit.started_at ? new Date(audit.started_at).toLocaleString('fr-FR') : '-'}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-foreground">{audit.agent_name || '-'}</p>
                      <p className="text-xs text-muted-foreground">{audit.device_name || '-'}</p>
                      <p className="text-xs text-muted-foreground">Statut: {audit.status}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">{audit.total_payments}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{formatMoney(audit.expected_amount || 0)}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{formatMoney(audit.declared_amount || 0)}</td>
                    <td className={`px-4 py-3 text-sm font-medium ${audit.gap_abs_amount > 0 ? 'text-destructive' : 'text-success'}`}>
                      {formatMoney(audit.gap_amount || 0)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                          audit.audit_status === 'balanced'
                            ? 'bg-success/10 text-success'
                            : audit.audit_status === 'warning'
                              ? 'bg-warning/10 text-warning'
                              : 'bg-destructive/10 text-destructive'
                        }`}
                      >
                        {audit.audit_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Pagination
          currentPage={pagination.current_page}
          totalPages={pagination.total_pages}
          basePath="/admin/financial_audits"
          query={query}
        />
      </div>
    </>
  )
}
